import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendEmailVerification,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { auth, firebaseEnabled } from '../firebase/firebaseConfig';


const LAST_AUTH_KEY = 'databiqs_last_auth_at';
const SESSION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const AuthContext = createContext(null);

const isPasswordAccount = (user) => !!user && user.providerData?.[0]?.providerId === 'password';

/** True once the visitor is allowed to submit a gated lead form: signed in, and
 * verified if they're a password account (Google accounts are verified by Google). */
const isAuthSatisfied = (user) => !!user && (!isPasswordAccount(user) || user.emailVerified);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(firebaseEnabled);

  useEffect(() => {
    if (!firebaseEnabled) {
      setLoading(false);
      return undefined;
    }

    setPersistence(auth, browserLocalPersistence).catch(() => {});

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      if (nextUser) {
        const lastAuthAt = Number(localStorage.getItem(LAST_AUTH_KEY) || 0);
        if (lastAuthAt && Date.now() - lastAuthAt > SESSION_MAX_AGE_MS) {
          signOut(auth).catch(() => {});
          setUser(null);
          setLoading(false);
          return;
        }
        if (!lastAuthAt) localStorage.setItem(LAST_AUTH_KEY, String(Date.now()));
      } else {
        localStorage.removeItem(LAST_AUTH_KEY);
      }
      setUser(nextUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const markAuthed = () => localStorage.setItem(LAST_AUTH_KEY, String(Date.now()));

  const signUp = useCallback(async (email, password) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(cred.user);
    markAuthed();
    return cred.user;
  }, []);

  const signIn = useCallback(async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    markAuthed();
    return cred.user;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    markAuthed();
    return cred.user;
  }, []);

  const resendVerification = useCallback(async () => {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser);
  }, []);

  /** Re-pulls the user record from Firebase so a just-verified emailVerified flag shows up.
   * Firebase's reload() mutates the existing currentUser object in place rather than
   * returning a new one, so setUser(auth.currentUser) would pass React the same
   * reference it already has and silently skip the re-render — clone it so React
   * actually notices emailVerified flipped to true. */
  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return null;
    await auth.currentUser.reload();
    const refreshed = auth.currentUser;
    setUser(refreshed ? { ...refreshed } : null);
    return refreshed;
  }, []);

  const signOutUser = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = {
    user,
    loading,
    firebaseEnabled,
    isAuthSatisfied: isAuthSatisfied(user),
    isPasswordAccount: isPasswordAccount(user),
    signUp,
    signIn,
    signInWithGoogle,
    signOutUser,
    resendVerification,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
